/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useRef } from "react";

/**
 * Every enabled overlay pushes onto this stack and a single shared listener
 * dispatches Escape to the top-most one only. Binding a listener per overlay
 * instead would let one press close a whole nest at once — a select inside a
 * popover would take the popover down with it.
 *
 * Order is enable-order, not z-order: an overlay opened from inside another
 * one enables later, so it sits on top, which is what dismissal should follow.
 */
const stack = [];
let isListening = false;

const onKeyDown = (event) => {
  if (event.key !== "Escape") return;

  const top = stack[stack.length - 1];
  if (!top) return;

  event.preventDefault();
  top.current(event);
};

const push = (entry) => {
  stack.push(entry);

  if (!isListening) {
    document.addEventListener("keydown", onKeyDown);
    isListening = true;
  }
};

const remove = (entry) => {
  const index = stack.indexOf(entry);
  if (index !== -1) {
    stack.splice(index, 1);
  }

  if (stack.length === 0 && isListening) {
    document.removeEventListener("keydown", onKeyDown);
    isListening = false;
  }
};

/**
 * Calls `handler` on Escape while `enabled` (default true), but only when this
 * is the top-most enabled overlay. For dismissible overlays — modals, popovers,
 * menus, selects.
 *
 * Read through a ref, so the entry stays stable across renders and always calls
 * the latest handler — callers needn't memoize.
 */
export const useEscapeKey = (handler, enabled = true) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return undefined;

    push(handlerRef);
    return () => remove(handlerRef);
  }, [enabled]);
};
